import BigNumber from "bignumber.js"
import cn from "classnames"
import throttle from "lodash-es/throttle"
import React, { useCallback, useState, useEffect, useRef, useMemo } from "react"
import { clamp } from "@/common/util/misc"
import { InfoOutline } from "@/skylticons"
import "./slider.css"

const stopEvents = ["mouseup", "touchend", "touchcancel"]
const throttleInterval = 50

interface SliderProps {
  /** Optional label above slider */
  label?: string
  /** Optional tooltip next to label */
  tooltip?: string
  /** Slider is in disabled state */
  disabled?: boolean
  /** Slider is in skeleton state */
  skeleton?: boolean
  /** Icon to use instead of min range label */
  rangeMinIcon?: React.ComponentType
  /** Icon to use instead of max range label */
  rangeMaxIcon?: React.ComponentType
  /** Current value */
  value?: number
  /** Minimum allowed value */
  min: number
  /** Maximum allowed value */
  max: number
  /** Granularity of the value when changing via arrow keys or dragging the slider */
  step?: number
  /** Callback fired when user changes value */
  onChange?: (value: number) => void
  /** Hide input */
  hideInput?: boolean
  /** Hide range labels */
  hideRangeLabels?: boolean
  /** Whether to place input on the right side of the slider */
  inputRight?: boolean
  /** Optional custom range of allowed values */
  customRange?: number[]
  /** Enabled min & max will define the enabled range on the slider track */
  enabledMin?: number
  enabledMax?: number
  className?: string
  inputId?: string
  inputName?: string
  ariaLabelInput?: string
}

export const Slider: React.FC<SliderProps> = ({
  label,
  tooltip,
  className,
  inputId,
  inputName,
  ariaLabelInput,
  rangeMinIcon,
  rangeMaxIcon,
  value,
  min,
  max,
  step,
  onChange,
  disabled,
  skeleton,
  hideInput,
  hideRangeLabels,
  inputRight,
  customRange,
  enabledMin,
  enabledMax,
}) => {
  value = value || 0
  const disableInteraction = disabled || skeleton
  const range = max - min
  const percent =
    customRange && customRange.length > 1
      ? (customRange.indexOf(value) / (customRange.length - 1)) * 100
      : clamp((value - min) / (range / 100), 0, 100)
  const enabledPercent = (val: number) => {
    if (customRange && customRange.length > 1) {
      return (customRange.indexOf(val) / (customRange.length - 1)) * 100
    } else {
      return clamp((val - min) / (range / 100), 0, 100)
    }
  }

  const enabledFrom = enabledMin !== undefined ? enabledPercent(enabledMin) : 0
  const enabledTo = enabledMax !== undefined ? enabledPercent(enabledMax) : percent

  const [inputValue, setInputValue] = useState(value.toString())
  const [updateFromProps, setUpdateFromProps] = useState(false)
  const [dragging, setDragging] = useState(false)
  const sliderEl = useRef<HTMLDivElement>(null)
  const trackEl = useRef<HTMLDivElement>(null)
  const inputEl = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (value !== undefined && value !== null && document.activeElement !== inputEl.current) {
      setInputValue(value.toString())
      setUpdateFromProps(false)
    }
  }, [value, updateFromProps, setInputValue, setUpdateFromProps])

  const updateValue = useCallback(
    (newValue: number) => {
      if (customRange && customRange.length) {
        const getNearestValue = (val: number) =>
          customRange.reduce((prev, curr) => (Math.abs(curr - val) < Math.abs(prev - val) ? curr : prev))
        newValue = getNearestValue(newValue)
      }

      if (onChange) {
        onChange(newValue)
      }
    },
    [onChange, customRange]
  )

  const submitValue = useCallback(
    (newValue: string) => {
      if (onChange) {
        const parsedValue = parseFloat(newValue)
        onChange(isNaN(parsedValue) ? 1 : parsedValue)
        setUpdateFromProps(true)
      }
    },
    [onChange, setUpdateFromProps]
  )

  const blur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const value = parseFloat(e.target.value)
      const clampedValue = Math.min(Math.max(value, min), max)

      submitValue(clampedValue.toString())
    },
    [submitValue, min, max]
  )

  const submit = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        const value = parseFloat(e.currentTarget.value)
        const clampedValue = Math.min(Math.max(value, min), max)
        const stringValue = clampedValue.toString()

        setInputValue(stringValue)

        submitValue(stringValue)
      }
    },
    [min, max, submitValue]
  )

  const inputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (disableInteraction) {
        return
      }

      setInputValue(e.target.value)

      const numericValue = parseFloat(e.target.value)

      // allow any number to be entered so user is not interrupted in the middle of entering the value -
      // for example when range is 50-100 and user is trying to enter 75 then we shouldn't clamp immediately when
      // 7 is entered
      if (!isNaN(numericValue)) {
        updateValue(numericValue)
      }
    },
    [setInputValue, disableInteraction, updateValue]
  )

  const keyDown = useMemo(
    () =>
      throttle(
        (e: React.KeyboardEvent<HTMLDivElement | HTMLInputElement>) => {
          if (disableInteraction || value === undefined || value === null) {
            return
          }

          if (!customRange) {
            let delta = 0
            const deltaStep = step || 1

            if (["ArrowDown", "ArrowLeft"].includes(e.key)) {
              delta = -deltaStep
            } else if (["ArrowUp", "ArrowRight"].includes(e.key)) {
              delta = deltaStep
            } else {
              return
            }

            const valueBig = new BigNumber(value)

            // ensure entering custom value in input doesn't skip a step
            const newValue = (
              delta > 0 ? valueBig.dividedBy(deltaStep).integerValue(BigNumber.ROUND_FLOOR).times(deltaStep) : valueBig
            )
              .plus(delta)
              .toNumber()

            if (newValue > max || newValue < min) {
              return
            }

            updateValue(newValue)
          } else {
            // customRange step handling
            const currentIndex = customRange.indexOf(value)
            if (currentIndex === -1) return

            let newIndex = currentIndex
            if (["ArrowDown", "ArrowLeft"].includes(e.key)) {
              newIndex = currentIndex - 1
            } else if (["ArrowUp", "ArrowRight"].includes(e.key)) {
              newIndex = currentIndex + 1
            }

            if (newIndex >= 0 && newIndex < customRange.length) {
              updateValue(customRange[newIndex])
            }
          }
        },
        throttleInterval,
        { leading: true, trailing: false }
      ),
    [disableInteraction, step, value, updateValue, min, max, customRange]
  )

  const drag = useMemo(
    () =>
      throttle(
        (e: MouseEvent | TouchEvent) => {
          if (disableInteraction || !trackEl.current || value === undefined || value === null) {
            return
          }

          let clientX

          if ("clientX" in e) {
            clientX = e.clientX
          } else if ("touches" in e && 0 in e.touches && "clientX" in e.touches[0]) {
            clientX = e.touches[0].clientX
          } else {
            return
          }

          const boundingRect = trackEl.current.getBoundingClientRect()
          const width = boundingRect.right - boundingRect.left
          const leftOffset = clientX - boundingRect.left
          const percent = new BigNumber(clamp(leftOffset / width, 0, 1))
          const dragStep = step || 1
          let newValue = percent.times(range).plus(min)

          // force it to a step value
          newValue = newValue.dividedBy(dragStep).integerValue(BigNumber.ROUND_HALF_UP).times(dragStep)

          updateValue(newValue.toNumber())
        },
        throttleInterval,
        { leading: true, trailing: false }
      ),
    [disableInteraction, range, value, min, step, updateValue]
  )

  const dragStop = useCallback(() => {
    if (!sliderEl.current) {
      return
    }

    for (const event of stopEvents) {
      sliderEl.current.ownerDocument?.removeEventListener(event, dragStop)
    }

    sliderEl.current.ownerDocument?.removeEventListener("mousemove", drag)
    sliderEl.current.ownerDocument?.removeEventListener("touchmove", drag)
  }, [drag])

  const dragStart = useCallback(
    (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
      if (disableInteraction || !sliderEl.current) {
        return
      }

      for (const event of stopEvents) {
        sliderEl.current.ownerDocument?.addEventListener(event, dragStop)
      }

      sliderEl.current.ownerDocument?.addEventListener("mousemove", drag)
      sliderEl.current.ownerDocument?.addEventListener("touchmove", drag)

      drag(e.nativeEvent)
    },
    [disableInteraction, drag, dragStop]
  )

  // dumb hack to make safari stop changing to text cursor on drag
  // https://stackoverflow.com/questions/47295211/safari-wrong-cursor-when-dragging

  const ignoreSelect = useCallback(() => {
    setDragging(true)
  }, [setDragging])

  const restoreSelect = useCallback(() => {
    setDragging(true)
  }, [setDragging])

  const mouseOver = useCallback(() => {
    document.onselectstart = function () {
      return false
    }
  }, [])

  const mouseOut = useCallback(() => {
    if (!dragging) {
      document.onselectstart = function () {
        return true
      }
    }
  }, [dragging])

  return (
    <div
      className={cn("sm-slider flex flex-col disabled:pointer-events-none", className, {
        disabled,
        "no-range-labels": hideRangeLabels,
      })}
    >
      {label && (
        <label className="mb-1 flex items-center text-ink-100">
          <span className={cn({ skeleton })}>{label}</span>{" "}
          {tooltip && !skeleton && (
            <div className="group relative inline-block">
              <InfoOutline className="ml-1 h-4 w-4 hover:text-blue-500" />
              <div className="absolute bottom-full left-1/2 hidden w-60 -translate-x-1/2 rounded bg-black px-2 py-1 text-xs text-white group-hover:block">
                {tooltip}
              </div>
            </div>
          )}
        </label>
      )}
      <div
        className={cn("flex", {
          "flex-col": !inputRight,
          "flex-row": inputRight,
        })}
      >
        <div className="slider-container flex flex-auto items-center">
          {!hideRangeLabels && (
            <div
              className={cn("slider-range-icon min mb-0 flex h-6 w-6 items-center justify-center text-ink-100", {
                skeleton,
              })}
            >
              {rangeMinIcon ? React.createElement(rangeMinIcon) : min}
            </div>
          )}
          <div
            className={cn(
              "slider-range relative my-0 ml-0 mr-5 min-w-[150px] flex-1 cursor-pointer select-none py-2 pl-2.5 hover:outline-none focus:outline-none active:cursor-grabbing",
              { skeleton }
            )}
            tabIndex={0}
            ref={sliderEl}
            onKeyDown={keyDown}
            onMouseDown={dragStart}
            onTouchStart={dragStart}
            onDragStart={ignoreSelect}
            onDragEnd={restoreSelect}
            onMouseOver={mouseOver}
            onMouseOut={mouseOut}
          >
            <div className="slider-track relative h-1 w-full rounded bg-white-20 disabled:bg-white-5" ref={trackEl}>
              {enabledMin !== undefined && enabledMax !== undefined ? (
                <div
                  className="slider-track-fill absolute top-0 h-1 rounded bg-ink-100 disabled:bg-white-20"
                  style={{
                    left: `${enabledFrom}%`,
                    width: `${enabledTo - enabledFrom}%`,
                  }}
                />
              ) : (
                <div
                  className="slider-track-fill absolute top-0 h-1 rounded bg-ink-100 disabled:bg-white-20"
                  style={{ width: `${percent}%` }}
                />
              )}
              <div
                className="slider-thumb absolute top-1/2 h-5 w-5 -translate-y-1/2 rounded-[20px] bg-ink-100 hover:scale-[1.2] focus:scale-[1.2] disabled:bg-white-20"
                style={{ left: `${percent}%`, transform: "translate(-50%, -50%)" }}
              />
            </div>
          </div>
          {!hideRangeLabels && (
            <div className={cn("slider-range-icon max", { skeleton })}>
              {rangeMaxIcon ? React.createElement(rangeMaxIcon) : max}
            </div>
          )}
        </div>
        <div>
          {!hideInput && !skeleton && (
            <input
              type="number"
              ref={inputEl}
              id={inputId}
              name={inputName}
              aria-label={ariaLabelInput}
              min={min}
              max={max}
              step={step}
              value={inputValue}
              onKeyDown={submit}
              onBlur={blur}
              onChange={inputChange}
              disabled={disabled}
              className={cn(
                "box-border rounded border border-solid border-white-20 px-2 text-center text-ink-100 focus:border focus:border-solid focus:border-blue-500 focus:outline-none disabled:bg-white-5 disabled:text-ink-50",
                { "ml-1 mt-0 w-10": inputRight, "mt-4 w-full": !inputRight }
              )}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default Slider
